import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useWorkspace } from "@/hooks/use-workspace";
import { 
  CreditCard, 
  Check, 
  AlertCircle, 
  ExternalLink, 
  Loader2, 
  Wallet,
  ArrowRight,
  RefreshCw,
  X,
  CircleDollarSign,
} from "lucide-react";
import { SiStripe, SiPaypal } from "react-icons/si";

interface PaymentSettings {
  id?: string;
  stripeConnectAccountId?: string;
  stripeConnectStatus?: string;
  stripeConnectOnboardingComplete?: boolean;
  stripeConnectPayoutsEnabled?: boolean;
  stripeConnectChargesEnabled?: boolean;
  stripeConnectedAt?: string;
  paypalMerchantId?: string;
  paypalEmail?: string;
  paypalClientId?: string;
  paypalStatus?: string;
  paypalConnectedAt?: string;
  preferredProvider?: string;
  enableInvoicePayments?: boolean;
  enableDepositCollection?: boolean;
  depositPercentage?: number;
}

interface PaymentPortalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

// Stripe Connect webhook health badge (task #669). Reads the
// per-workspace probe state the hourly health scheduler writes;
// surfaces a remediation hint when the wiring is unhealthy.
type WebhookHealthStatus = "healthy" | "missing_events" | "failing_deliveries" | "not_configured";
interface WebhookHealthResponse {
  status: WebhookHealthStatus;
  detail: string;
  lastCheckedAt: string | null;
  missedPaymentsLast24h: number;
}

function StripeWebhookHealthCard() {
  const { buildPath } = useWorkspace();
  const { data, isLoading } = useQuery<WebhookHealthResponse>({
    queryKey: [buildPath("/api/payment-settings/stripe/webhook-health")],
    refetchInterval: 60_000,
  });

  if (isLoading || !data) return null;

  const presentation: Record<
    WebhookHealthStatus,
    { label: string; tone: string; icon: React.ReactNode; remediation?: string }
  > = {
    healthy: {
      label: "Webhook receiving events",
      tone: "bg-green-50 dark:bg-green-900/10 border-green-200 text-green-800 dark:text-green-200",
      icon: <Check className="w-4 h-4 text-green-600" />,
    },
    missing_events: {
      label: "Webhook missing required events",
      tone: "bg-red-50 dark:bg-red-900/10 border-red-200 text-red-800 dark:text-red-200",
      icon: <AlertCircle className="w-4 h-4 text-red-600" />,
      remediation:
        "Re-run the Connect flow above, or open the Stripe Dashboard → Developers → Webhooks and subscribe the platform endpoint to checkout.session.completed.",
    },
    failing_deliveries: {
      label: "Webhook deliveries dropping",
      tone: "bg-amber-50 dark:bg-amber-900/10 border-amber-200 text-amber-900 dark:text-amber-100",
      icon: <AlertCircle className="w-4 h-4 text-amber-600" />,
      remediation:
        "Our backstop reconciler caught customer payments your Stripe webhook should have delivered. Open Stripe Dashboard → Developers → Webhooks to see failed deliveries; payments are still being captured automatically by our 5-minute reconciliation poll.",
    },
    not_configured: {
      label: "Stripe Connect not configured",
      tone: "bg-muted border-border text-muted-foreground",
      icon: <AlertCircle className="w-4 h-4" />,
    },
  };
  const ui = presentation[data.status];

  return (
    <Card className={ui.tone} data-testid={`card-stripe-webhook-health-${data.status}`}>
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          {ui.icon}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-medium" data-testid="text-webhook-health-label">{ui.label}</div>
              {data.lastCheckedAt && (
                <div className="text-xs opacity-75" data-testid="text-webhook-health-checked-at">
                  Checked {new Date(data.lastCheckedAt).toLocaleString()}
                </div>
              )}
            </div>
            {data.detail && (
              <div className="text-xs mt-1 opacity-90" data-testid="text-webhook-health-detail">
                {data.detail}
              </div>
            )}
            {ui.remediation && (
              <div className="text-xs mt-2" data-testid="text-webhook-health-remediation">
                {ui.remediation}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function PaymentPortalDialog({ open, onOpenChange, inPanel = false }: PaymentPortalDialogProps) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [activeStep, setActiveStep] = useState<'overview' | 'stripe' | 'paypal'>('overview');
  const [paypalEmail, setPaypalEmail] = useState("");
  const [paypalMeUsername, setPaypalMeUsername] = useState("");
  const [paypalClientId, setPaypalClientId] = useState("");
  const [paypalClientSecret, setPaypalClientSecret] = useState("");
  const [paypalMerchantId, setPaypalMerchantId] = useState("");

  const { data: settings, isLoading, refetch } = useQuery<PaymentSettings>({
    queryKey: [buildPath("/api/payment-settings")],
    enabled: open,
  });

  useEffect(() => {
    if (settings) {
      setPaypalEmail(settings.paypalEmail || "");
      setPaypalMeUsername((settings as any).paypalMeUsername || "");
      setPaypalClientId(settings.paypalClientId || "");
      setPaypalClientSecret((settings as any).paypalClientSecret || "");
      setPaypalMerchantId(settings.paypalMerchantId || "");
    }
  }, [settings]);

  // Auto-check Stripe status when returning from Stripe onboarding or when status is pending
  useEffect(() => {
    if (!open) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const stripeSuccess = urlParams.get('stripe_success');
    const stripeRefresh = urlParams.get('stripe_refresh');
    
    // If returning from Stripe onboarding, auto-check status
    if (stripeSuccess === 'true' || stripeRefresh === 'true') {
      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('stripe_success');
      newUrl.searchParams.delete('stripe_refresh');
      window.history.replaceState({}, '', newUrl.toString());
      
      // Check status after a brief delay to allow Stripe webhook to process
      setTimeout(() => {
        checkStripeStatusMutation.mutate();
      }, 1000);
    }
    // Also auto-check if status is pending (user may have completed onboarding)
    else if (settings?.stripeConnectStatus === 'pending' && settings?.stripeConnectAccountId) {
      checkStripeStatusMutation.mutate();
    }
  }, [open, settings?.stripeConnectStatus, settings?.stripeConnectAccountId]);

  const connectStripeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", buildPath("/api/payment-settings/stripe/connect"));
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to start Stripe connection");
      }
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        // Try to open in new window, fallback to redirect if blocked
        const newWindow = window.open(data.url, '_blank');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Popup was blocked - redirect in same window
          toast({
            title: "Redirecting to Stripe...",
            description: "You'll be redirected to complete setup. Return here when finished.",
          });
          setTimeout(() => {
            window.location.href = data.url;
          }, 1500);
        } else {
          toast({
            title: "Stripe Onboarding Started",
            description: "Complete the setup in the new window. Return here when finished.",
          });
        }
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to start Stripe connection. Please try again.",
        variant: "destructive",
      });
    },
  });

  const checkStripeStatusMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("GET", buildPath("/api/payment-settings/stripe/status"));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/payment-settings")] });
      toast({
        title: "Status Updated",
        description: "Stripe connection status has been refreshed.",
      });
    },
  });

  const disconnectStripeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", buildPath("/api/payment-settings/stripe/disconnect"));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/payment-settings")] });
      toast({
        title: "Stripe Disconnected",
        description: "Your Stripe account has been disconnected.",
      });
    },
  });

  const connectPaypalMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", buildPath("/api/payment-settings/paypal/connect"), {
        paypalEmail,
        paypalMeUsername,
        paypalClientId,
        paypalClientSecret,
        paypalMerchantId,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/payment-settings")] });
      setActiveStep('overview');
      toast({
        title: "PayPal Connected",
        description: "Your PayPal account is now connected.",
      });
    },
    onError: () => {
      toast({
        title: "Connection Failed",
        description: "Failed to save PayPal settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  const disconnectPaypalMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", buildPath("/api/payment-settings/paypal/disconnect"));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/payment-settings")] });
      setPaypalEmail("");
      setPaypalMeUsername("");
      setPaypalClientId("");
      setPaypalMerchantId("");
      toast({
        title: "PayPal Disconnected",
        description: "Your PayPal account has been disconnected.",
      });
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: Partial<PaymentSettings>) => {
      const res = await apiRequest("PATCH", buildPath("/api/payment-settings"), updates);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/payment-settings")] });
      toast({
        title: "Settings Saved",
        description: "Payment settings have been updated.",
      });
    },
  });

  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/10 text-green-600 border-green-200"><Check className="w-3 h-3 mr-1" /> Connected</Badge>;
      case 'pending':
        return <Badge variant="outline" className="border-yellow-400 text-yellow-600"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Pending</Badge>;
      case 'restricted':
        return <Badge variant="outline" className="border-orange-400 text-orange-600"><AlertCircle className="w-3 h-3 mr-1" /> Restricted</Badge>;
      default:
        return <Badge variant="outline" className="text-muted-foreground">Not Connected</Badge>;
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="text-center pb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Wallet className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold">Payment Providers</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Connect your payment accounts to receive payments from your clients
        </p>
      </div>

      <div className="grid gap-4">
        <Card className="hover-elevate cursor-pointer" onClick={() => setActiveStep('stripe')} data-testid="card-stripe-provider">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
                  <SiStripe className="w-6 h-6 text-[#635BFF]" />
                </div>
                <div>
                  <h4 className="font-medium">Stripe</h4>
                  <p className="text-sm text-muted-foreground">Accept credit cards, bank transfers</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(settings?.stripeConnectStatus)}
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-elevate cursor-pointer" onClick={() => setActiveStep('paypal')} data-testid="card-paypal-provider">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#003087]/10 flex items-center justify-center">
                  <SiPaypal className="w-6 h-6 text-[#003087]" />
                </div>
                <div>
                  <h4 className="font-medium">PayPal</h4>
                  <p className="text-sm text-muted-foreground">Accept PayPal payments</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(settings?.paypalStatus)}
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/*
        Payment Options (Enable Invoice Payments, Enable Deposit Collection,
        Deposit Percentage) used to live here. They moved to Admin → Invoice
        Settings → General Settings so every invoice-shaped toggle lives in
        one place. The connector cards (Stripe, PayPal) are intentionally
        still rendered here because they are reused inside Admin →
        Integrations → Overview/Workspace Services, which mounts this same
        dialog.
      */}
    </div>
  );

  const renderStripeSetup = () => (
    <div className="space-y-6">
      <Button 
        variant="ghost" 
        size="sm" 
        className="mb-2"
        onClick={() => setActiveStep('overview')}
        data-testid="button-back-to-overview"
      >
        <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
        Back to Overview
      </Button>

      <div className="flex items-center gap-4 pb-4">
        <div className="w-16 h-16 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
          <SiStripe className="w-8 h-8 text-[#635BFF]" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Connect Stripe</h3>
          <p className="text-sm text-muted-foreground">
            Accept credit cards, debit cards, and bank transfers
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm font-medium">Status:</span>
        {getStatusBadge(settings?.stripeConnectStatus)}
        {settings?.stripeConnectStatus === 'active' && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => checkStripeStatusMutation.mutate()}
            disabled={checkStripeStatusMutation.isPending}
            data-testid="button-refresh-stripe-status"
          >
            <RefreshCw className={`w-4 h-4 ${checkStripeStatusMutation.isPending ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>

      {settings?.stripeConnectStatus === 'active' ? (
        <div className="space-y-4">
          <Card className="bg-green-50 dark:bg-green-900/10 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-800 dark:text-green-200">Stripe is Connected</h4>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Your account is ready to receive payments.
                  </p>
                  {settings.stripeConnectedAt && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                      Connected on {new Date(settings.stripeConnectedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <StripeWebhookHealthCard />


          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
              data-testid="button-open-stripe-dashboard"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Stripe Dashboard
            </Button>
            <Button 
              variant="outline"
              onClick={() => disconnectStripeMutation.mutate()}
              disabled={disconnectStripeMutation.isPending}
              className="text-destructive hover:text-destructive"
              data-testid="button-disconnect-stripe"
            >
              <X className="w-4 h-4 mr-2" />
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">How to Connect</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">1</div>
                <p className="text-sm">Click "Connect with Stripe" below to start the secure setup</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">2</div>
                <p className="text-sm">Create or log in to your Stripe account in the new window</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">3</div>
                <p className="text-sm">Complete the verification process (usually takes 5-10 minutes)</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">4</div>
                <p className="text-sm">Return here and click "Check Status" to verify the connection</p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button 
              className="flex-1 bg-[#635BFF] hover:bg-[#5851db]"
              onClick={() => connectStripeMutation.mutate()}
              disabled={connectStripeMutation.isPending}
              data-testid="button-connect-stripe"
            >
              {connectStripeMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <SiStripe className="w-4 h-4 mr-2" />
              )}
              Connect with Stripe
            </Button>
            {settings?.stripeConnectStatus === 'pending' && (
              <Button 
                variant="outline"
                onClick={() => checkStripeStatusMutation.mutate()}
                disabled={checkStripeStatusMutation.isPending}
                data-testid="button-check-stripe-status"
              >
                {checkStripeStatusMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Check Status
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderPaypalSetup = () => (
    <div className="space-y-6">
      <Button 
        variant="ghost" 
        size="sm" 
        className="mb-2"
        onClick={() => setActiveStep('overview')}
        data-testid="button-back-paypal"
      >
        <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
        Back to Overview
      </Button>

      <div className="flex items-center gap-4 pb-4">
        <div className="w-16 h-16 rounded-lg bg-[#003087]/10 flex items-center justify-center">
          <SiPaypal className="w-8 h-8 text-[#003087]" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Connect PayPal</h3>
          <p className="text-sm text-muted-foreground">
            Accept PayPal payments from your clients
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm font-medium">Status:</span>
        {getStatusBadge(settings?.paypalStatus)}
      </div>

      {settings?.paypalStatus === 'active' ? (
        <div className="space-y-4">
          <Card className="bg-green-50 dark:bg-green-900/10 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-800 dark:text-green-200">PayPal is Connected</h4>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    {settings.paypalEmail && `Email: ${settings.paypalEmail}`}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {(settings as any).paypalMeUsername && `PayPal.me: paypal.com/paypalme/${(settings as any).paypalMeUsername}`}
                  </p>
                  {settings.paypalConnectedAt && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                      Connected on {new Date(settings.paypalConnectedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => window.open('https://www.paypal.com/businessmanage/account/aboutBusiness', '_blank')}
              data-testid="button-open-paypal"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open PayPal Dashboard
            </Button>
            <Button 
              variant="outline"
              onClick={() => disconnectPaypalMutation.mutate()}
              disabled={disconnectPaypalMutation.isPending}
              className="text-destructive hover:text-destructive"
              data-testid="button-disconnect-paypal"
            >
              <X className="w-4 h-4 mr-2" />
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">How to Get Your PayPal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">1</div>
                <div>
                  <p className="text-sm">Log in to your <a href="https://developer.paypal.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">PayPal Developer Dashboard</a></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">2</div>
                <p className="text-sm">Go to Apps & Credentials and create or select an app</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">3</div>
                <p className="text-sm">Copy your Client ID from the app details</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">4</div>
                <p className="text-sm">Enter your PayPal business email and Client ID below</p>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <div>
              <Label htmlFor="paypal-email">PayPal Business Email</Label>
              <Input
                id="paypal-email"
                type="email"
                placeholder="your-business@email.com"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                data-testid="input-paypal-email"
              />
            </div>
            <div>
              <Label htmlFor="paypal-me-username">PayPal.me Username <span className="text-destructive">*</span></Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">paypal.com/paypalme/</span>
                <Input
                  id="paypal-me-username"
                  placeholder="yourname"
                  value={paypalMeUsername}
                  onChange={(e) => setPaypalMeUsername(e.target.value)}
                  data-testid="input-paypal-me-username"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Your PayPal.me link username (not your email)</p>
            </div>
            <div>
              <Label htmlFor="paypal-client-id">Client ID</Label>
              <Input
                id="paypal-client-id"
                placeholder="AYSq3RDGsmBLJE..."
                value={paypalClientId}
                onChange={(e) => setPaypalClientId(e.target.value)}
                data-testid="input-paypal-client-id"
              />
              <p className="text-xs text-muted-foreground mt-1">From your PayPal Developer app — required for server-verified checkout</p>
            </div>
            <div>
              <Label htmlFor="paypal-client-secret">Client Secret</Label>
              <Input
                id="paypal-client-secret"
                type="password"
                placeholder="EBWKjlELKMYqRNQ8sYvFVwdeoe4KdU3m..."
                value={paypalClientSecret}
                onChange={(e) => setPaypalClientSecret(e.target.value)}
                data-testid="input-paypal-client-secret"
              />
              <p className="text-xs text-muted-foreground mt-1">Stored securely — enables server-verified PayPal Checkout (no honor-system)</p>
            </div>
            <div>
              <Label htmlFor="paypal-merchant-id">Merchant ID (Optional)</Label>
              <Input
                id="paypal-merchant-id"
                placeholder="ABCD1234567890"
                value={paypalMerchantId}
                onChange={(e) => setPaypalMerchantId(e.target.value)}
                data-testid="input-paypal-merchant-id"
              />
              <p className="text-xs text-muted-foreground mt-1">Found in Account Settings</p>
            </div>
          </div>

          <Button 
            className="w-full bg-[#003087] hover:bg-[#002569]"
            onClick={() => connectPaypalMutation.mutate()}
            disabled={connectPaypalMutation.isPending || (!paypalEmail && !paypalClientId && !paypalMeUsername)}
            data-testid="button-connect-paypal"
          >
            {connectPaypalMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <SiPaypal className="w-4 h-4 mr-2" />
            )}
            Connect PayPal
          </Button>
        </div>
      )}
    </div>
  );

  const innerContent = (<>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CircleDollarSign className="w-5 h-5" />
            Payment Portal
          </DialogTitle>
          <DialogDescription>
            Connect your payment accounts to receive payments from clients
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {activeStep === 'overview' && renderOverview()}
            {activeStep === 'stripe' && renderStripeSetup()}
            {activeStep === 'paypal' && renderPaypalSetup()}
          </>
        )}
    </>
  );

  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        {innerContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        {innerContent}
      </DialogContent>
    </Dialog>
  );
}
